// collector-channel.service.ts

import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, forkJoin, Observable, take, BehaviorSubject, tap } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import {
  GET_COLLECTORS,
  GET_NOTIFICATION_CHANNELS,
} from '@shared/config/channelCOnfigCollectors.config';
import { ChannelConfigSlim, CollectorSlim } from '@shared/models/collectorsCheck.model';

export const CHANNEL_KEY_TO_CONFIG_VALUE: Record<string, string> = {
  sendSMS: 'sms',
  sendEmail: 'email',
  sendCertifiedEmail: 'sendCertifiedEmail',
  sendCertifiedSMS: 'sendCertifiedSMS',
};
const REQUIRED_CHANNELS = ['sendSMS', 'sendEmail', 'sendCertifiedEmail', 'sendCertifiedSMS'];

@Injectable({ providedIn: 'root' })
export class CollectorChannelService {
  private collectors: CollectorSlim[] = [];
  private channelConfigMap: Record<string, Set<string>> = {};
  private validationSubject = new BehaviorSubject<Record<string, string[]>>({});

  constructor(
    private apollo: Apollo,
    private route: ActivatedRoute,
  ) {}

  loadData(): Observable<void> {
    return forkJoin([this.loadCollectors(), this.loadChannelConfigs()]).pipe(map(() => void 0));
  }

  loadCollectors(): Observable<CollectorSlim[]> {
    const tenantId = this.route.snapshot.queryParamMap.get('tenant');

    if (!tenantId) {
      throw new Error('Tenant ID is missing from route parameters');
    }

    return this.apollo.query({ query: GET_COLLECTORS, variables: { tenantId } }).pipe(
      take(1),
      map((res: any) => res.data.searchCollectors),
      map((collectors: CollectorSlim[]) => {
        this.collectors = collectors;
        return collectors;
      }),
    );
  }

  private loadChannelConfigs(): Observable<ChannelConfigSlim[]> {
    const tenantId = this.route.snapshot.queryParamMap.get('tenant');
    return this.apollo.query({ query: GET_NOTIFICATION_CHANNELS, variables: { tenantId } }).pipe(
      take(1),
      map((res: any) => res.data.channelConfigs),
      map((configs: ChannelConfigSlim[]) => {
        this.channelConfigMap = {};
        configs.forEach((config) => {
          let mappedChannel: string;
          switch (config.channel.toLowerCase()) {
            case 'certified_sms':
              mappedChannel = 'sendcertifiedsms';
              break;
            case 'certified_email':
              mappedChannel = 'sendcertifiedemail';
              break;
            default:
              mappedChannel = config.channel.toLowerCase();
          }
          config.collectors.forEach((collector) => {
            if (!this.channelConfigMap[collector.id]) {
              this.channelConfigMap[collector.id] = new Set();
            }
            this.channelConfigMap[collector.id].add(mappedChannel);
          });
        });
        return configs;
      }),
    );
  }

  getAllCollectorIds(): string[] {
    return this.collectors.map((c) => c.id);
  }

  collectorHasChannel(collectorId: string, channel: string): boolean {
    return this.channelConfigMap[collectorId]?.has(channel) ?? false;
  }

  validateCollectorsForChannel(collectorIds: string[], channel: string): string[] {
    return collectorIds
      .filter((id) => !this.collectorHasChannel(id, channel))
      .map((id) => this.collectors.find((c) => c.id === id)?.name || id);
  }

  //Give me all channels where some collector is missing it
  getChannelsNotFullyUsed(): string[] {
    const allCollectorIds = this.getAllCollectorIds();

    return REQUIRED_CHANNELS.filter((channel) => {
      const configValue = CHANNEL_KEY_TO_CONFIG_VALUE[channel].toLowerCase();

      return allCollectorIds.some((collectorId) => {
        return !this.collectorHasChannel(collectorId, configValue.toLowerCase());
      });
    });
  }

  validateTenantForChannel(channel: string): string[] {
    return this.validateCollectorsForChannel(this.getAllCollectorIds(), channel.toLowerCase());
  }

  setValidators(validators: Record<string, string[]>) {
    this.validationSubject.next(validators);
  }
}
